/**
 * Hook for reading and updating the tenant's required association types config.
 */

import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useToast} from '@/matic/components/ui/toast';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getRelayApi} from '../api/relay-api';
import {relayKeys} from '../query-keys';

export type RequiredAssociationType = {
  objectTypeId: string;
  name: string;
  prefix: string;
};

export function useRequiredAssociationTypes() {
  const tenantId = useRequiredTenantId();
  const api = getRelayApi();

  return useQuery<RequiredAssociationType[]>({
    queryKey: relayKeys.config.requiredTypes(tenantId),
    queryFn: async () => api.getRequiredAssociationTypes(),
  });
}

export function useUpdateRequiredAssociationTypes() {
  const tenantId = useRequiredTenantId();
  const queryClient = useQueryClient();
  const {toast} = useToast();
  const api = getRelayApi();

  return useMutation({
    // Preserve the selected types in the query cache immediately. The config
    // dropdown therefore never becomes disabled or unmounts between picks.
    mutationFn: async (types: RequiredAssociationType[]) =>
      api.updateRequiredAssociationTypes(
        types.map((type) => type.objectTypeId)
      ),
    async onMutate(nextTypes) {
      const queryKey = relayKeys.config.requiredTypes(tenantId);
      await queryClient.cancelQueries({queryKey});
      const previousTypes =
        queryClient.getQueryData<RequiredAssociationType[]>(queryKey);
      queryClient.setQueryData(queryKey, nextTypes);
      return {previousTypes, queryKey};
    },
    onSuccess(persistedTypes, nextTypes, context) {
      // A later selection may already have overwritten the cache with its
      // own optimistic value. Only reconcile with the server response if
      // this request's optimistic value is still the visible one.
      if (context && queryClient.getQueryData(context.queryKey) === nextTypes) {
        queryClient.setQueryData(context.queryKey, persistedTypes);
      }
    },
    onError(_error, nextTypes, context) {
      // A later selection may already have updated the cache. Only roll back
      // if this failed request still owns the visible optimistic value.
      if (context && queryClient.getQueryData(context.queryKey) === nextTypes) {
        queryClient.setQueryData(context.queryKey, context.previousTypes);
      }

      toast({title: 'Failed to update required types', variant: 'error'});
    },
  });
}
